import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { FiChevronLeft, FiChevronRight } from "react-icons/fi"
import { DateTime } from "luxon"

import useT from "utils/hooks/useT"
import { Container, Title, Select, Text } from "components/Form"
import { limsAudit } from "state_management"

// Audit logs can grow large, so the table is paginated.
const PAGE_SIZE = 25

// Read-only, filterable view of the immutable LIMS audit trail (F-A4). Entries are never
// editable/deletable here — there is no mutation control. SPEC-021 (B4): every entry is
// written SERVER-SIDE as part of the mutation it records (actor/initials/timestamp stamped
// from the authenticated member); the trail is only ever read here.
export default function AuditViewer() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const entries = useSelector((state) => state.limsAudit.entries)
  const members = useSelector((state) => state.members)

  const [userFilter, setUserFilter] = useState("")
  const [objectFilter, setObjectFilter] = useState("")
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [page, setPage] = useState(1)

  useEffect(() => {
    dispatch(limsAudit.fetch())
  }, [dispatch])

  // Reset to the first page whenever the filters change.
  useEffect(() => {
    setPage(1)
  }, [userFilter, objectFilter, fromDate, toDate])

  const memberOptions = Object.values(members)
    .filter((m) => !m.archived)
    .map((m) => ({ id: m.id, name: m.initials }))
  const objectTypeOptions = limsAudit.AUDIT_OBJECT_TYPES.map((typ) => ({
    id: typ,
    name: objectTypeLabel(typ, t),
  }))

  const filtered = entries.filter((e) => {
    if (userFilter && e.actorId !== userFilter) return false
    if (objectFilter && e.objectType !== objectFilter) return false
    const day = e.timestamp ? e.timestamp.slice(0, 10) : ""
    if (fromDate && day < fromDate) return false
    if (toDate && day > toDate) return false
    return true
  })

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageEntries = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  return (
    <div className="LimsAudit">
      <h1 className="LimsAudit-title">{t("limsAudit")}</h1>

      <div className="LimsAudit-filters">
        <Container>
          <Title>{t("member")}</Title>
          <Select
            isMulti={false}
            includeNone
            noneLabel={t("limsAuditAll")}
            collection={memberOptions}
            value={userFilter}
            onChange={(v) => setUserFilter(v[0] || "")}
            menuPlacement="bottom"
          />
        </Container>
        <Container>
          <Title>{t("limsAuditType")}</Title>
          <Select
            isMulti={false}
            includeNone
            noneLabel={t("limsAuditAll")}
            collection={objectTypeOptions}
            value={objectFilter}
            onChange={(v) => setObjectFilter(v[0] || "")}
            menuPlacement="bottom"
          />
        </Container>
        <div className="LimsAudit-filter--date">
          <Container>
            <Title>{t("from")}</Title>
            <Text
              type="date"
              value={fromDate}
              onChange={setFromDate}
              placeholder={t("dateFormatYyyyMmDd")}
            />
          </Container>
        </div>
        <div className="LimsAudit-filter--date">
          <Container>
            <Title>{t("to")}</Title>
            <Text
              type="date"
              value={toDate}
              onChange={setToDate}
              placeholder={t("dateFormatYyyyMmDd")}
            />
          </Container>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="LimsAudit-empty">{t("limsAuditNoEntries")}</p>
      ) : (
        <Table striped hover responsive className="LimsAudit-table">
          <thead>
            <tr>
              <th>{t("limsAuditTime")}</th>
              <th>{t("member")}</th>
              <th>{t("limsAuditAction")}</th>
              <th>{t("limsAuditObject")}</th>
              <th>{t("limsAuditReason")}</th>
            </tr>
          </thead>
          <tbody>
            {pageEntries.map((entry, i) => (
              <tr key={entry.id || i}>
                <td>{entry.timestamp ? DateTime.fromISO(entry.timestamp).toFormat("yyyy-LL-dd HH:mm") : ""}</td>
                <td>{entry.actorInitials}</td>
                <td>{t(entry.action)}</td>
                <td>{entry.objectLabel || objectTypeLabel(entry.objectType, t)}</td>
                <td>{entry.reason || ""}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {filtered.length > PAGE_SIZE && (
        <div className="LimsAudit-pagination">
          <Button size="sm" variant="light" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
            <FiChevronLeft />
          </Button>
          <span>{t("limsAuditPageInfo", { page: safePage, total: totalPages, count: filtered.length })}</span>
          <Button size="sm" variant="light" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>
            <FiChevronRight />
          </Button>
        </div>
      )}
    </div>
  )
}

function objectTypeLabel(typ, t) {
  if (!typ) return ""
  if (typ === "member") return t("member")
  if (typ === "product") return t("limsAuditTypeProduct")
  if (typ === "consumable") return t("limsAuditTypeConsumable")
  if (typ === "reagent") return t("limsAuditTypeReagent")
  if (typ === "oos") return t("limsAuditTypeOos")
  if (typ === "sample") return t("limsAuditTypeSample")
  if (typ === "result") return t("limsAuditTypeResult")
  if (typ === "forskrift") return t("limsAuditTypeForskrift")
  if (typ === "qaDecision") return t("limsAuditTypeQaDecision")
  if (typ === "batch") return t("limsAuditTypeBatch")
  if (typ === "rerun") return t("limsAuditTypeRerun")
  if (typ === "investigation") return t("limsAuditTypeInvestigation")
  if (typ === "capa") return t("limsAuditTypeCapa")
  if (typ === "sst") return t("limsAuditTypeSst")
  if (typ === "commentTemplate") return t("limsAuditTypeCommentTemplate")
  return typ
}
